/**
 * Returns a formatted string representation of the file size.
 * If the size is less than 1 MB, it will be displayed in KB.
 * If the size is 1 MB or greater, it will be displayed in MB.
 */
export function getFileSizeString(size: number): string {
    const kb = size / 1024;
    if (kb < 1024) {
        return `${kb.toFixed(2)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
}

/**
 * Takes a string and will wrap urls into html url syntax
 */
export function wrapUrlsInMarkdownLinks(text: string): string {
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
    return text.replace(urlRegex, (url) => {
        let href = url;
        if (url.startsWith("www.")) {
            href = "http://" + url;
        }
        return `<a target="_blank" href="${href}">${url}</a>`;
    });
}

/**
 * Strips HTML tags from a string and returns plain text
 * @param html - The HTML string to strip tags from
 * @returns Plain text without HTML tags
 */
export function stripHtmlTags(html: string | null | undefined): string {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
}

/**
 * Wraps tagged users (starting with @ and are email-like tags) in the given text with a span element for highlighting.
 * Ignores email addresses that do not start with an '@'.
 *
 * @param text - The input text
 * @returns string - The processed text with tagged users wrapped in span elements.
 * @example
 * tagWrapper("Hello @john.doe@test.com") // "Hello <span class="bg-purple-100 rounded-md px-1 py-0.5 uppercase font-mono text-sm"> <span class="mr-1">@</span>john.doe@test.com</span>!"
 * tagWrapper("Hello test@test.com") // "Hello test@test.com" (No wrapping since it doesn't start with '@')
 * tagWrapper("Hello test@test.com") // "Hello @another-string" (No wrapping since it start with '@' but it's not a valid email-like tag)
 */
export function tagWrapper(text: string): string {
    const regex = /(@[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

    return text.replace(regex, (match) => {
        const cleanedMatch = match.slice(1); // Remove the leading '@' from the match
        return `<span class="bg-purple-100 rounded-md px-1 py-0.5 uppercase font-mono text-sm"> <span class="mr-1">@</span>${cleanedMatch}</span>`;
    });
}

/**
 * Returns initials based on customer's name and first last name
 *
 * If string comes in format 'John Smith', the initials will be 'JS',
 * otherwise, if string is an email, the initials will be the first and
 * second letter of first name, example 'john.smith@example.com' will be 'JO'.
 *
 * @example
 * getInitials('John Doe') // JD
 */
export function getInitials(str = ""): string {
    if (!str) {
        return "";
    }
    let result: string;
    if (str.split(" ").length === 1) {
        result = str.split("")[0] + str.split("")[1];
    } else {
        result = str.split("")[0] + str.split(" ")[1].split("")[0];
    }
    return result.toUpperCase();
}


export function isProperMember(author: string): {
    employeeEmail: string | null;
    isProper: boolean;
} {
    if (!author || typeof author !== "string") {
        return { employeeEmail: null, isProper: false };
    }
    const trimmed = author.trim();
    const lower = trimmed.toLowerCase();
    const isProper = lower.endsWith("@proper.ai");
    return { employeeEmail: trimmed, isProper };
}
